import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import { useAuth } from '../../context/AuthContext'
import { useBranding } from '../../context/BrandingContext'
import { useOnboarding, onboardingStorage } from '../../services/onboardingService'
import OnboardingModal from './OnboardingModal'
import NewRequestModal from './NewRequestModal'
import '../../styles/client-pages.css'

// ── Inline SVG icons (match redesign prototype) ────────────────────────────────
const IcGlobe = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>
)
const IcEdit = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
)
const IcExternal = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
    <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
)
const IcCheck = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
)
const IcShield = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
)
const IcBolt = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>
)
const IcArrow = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
)
const IcArrowUp = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/>
  </svg>
)
const IcMessage = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
)
const IcChart = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
  </svg>
)
const IcSend = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
)
const IcPlus = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
)
const IcHelp = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
)

// ── Relative time helper ───────────────────────────────────────────────────────
const timeAgo = (iso) => {
  if (!iso) return ''
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'now'
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h`
  const d = Math.floor(h / 24)
  return `${d}d`
}

// ── Stat chip (prototype SoftStat) ─────────────────────────────────────────────
function SoftStat({ label, value, sub, delta, up, progress }) {
  return (
    <div className="stat-chip">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="lbl">{label}</div>
        {delta && (
          <span className={up ? 'delta-up' : 'delta-down'} style={{ fontSize: 11 }}>
            {up && <IcArrowUp />}{delta}
          </span>
        )}
      </div>
      <div className="num" style={{ marginTop: 6 }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>}
      {progress != null && (
        <div className="progress thin" style={{ marginTop: 10 }}><div style={{ width: `${progress}%` }} /></div>
      )}
    </div>
  )
}

// ── Usage bar (prototype style) ────────────────────────────────────────────────
function UsageBar({ label, used, total, unit = '' }) {
  const pct = total > 0 ? Math.min((used / total) * 100, 100) : 0
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 4 }}>
        <span>{label}</span>
        <span style={{ fontFamily: 'var(--font-mono)' }}>{used}{unit} / {total}{unit}</span>
      </div>
      <div className="progress thin"><div style={{ width: `${pct}%` }} /></div>
    </div>
  )
}

// ── Simple area sparkline ──────────────────────────────────────────────────────
function Sparkline({ data }) {
  if (!data || data.length < 2) {
    return <div style={{ height: 120, background: 'var(--border)', borderRadius: 8, opacity: .2 }} />
  }
  const W = 320, H = 120
  const max = Math.max(...data, 1)
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * W},${H - (v / max) * (H - 10)}`)
  const fill = [...pts, `${W},${H}`, `0,${H}`].join(' ')
  return (
    <div style={{ width: '100%' }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: 120 }}>
        <defs>
          <linearGradient id="cVisits" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#7C5CFF" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#7C5CFF" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <polygon points={fill} fill="url(#cVisits)" />
        <polyline points={pts.join(' ')} fill="none" stroke="#7C5CFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  )
}

// ── Request status badge config ────────────────────────────────────────────────
const STATUS_BADGE = {
  pending:       { label: 'Open',        tone: 'warning' },
  open:          { label: 'Open',        tone: 'warning' },
  approved:      { label: 'In progress', tone: 'info' },
  'in-progress': { label: 'In progress', tone: 'info' },
  in_progress:   { label: 'In progress', tone: 'info' },
  'in-review':   { label: 'In review',   tone: 'accent' },
  completed:     { label: 'Done',        tone: 'success' },
  rejected:      { label: 'Rejected',    tone: 'danger' },
}

// Request row: collapsed shows status + title (+ last admin reply if any).
// Click the container to expand an inline quick-reply; click the title to open
// the full request on the Requests page.
function ClientRequestRow({ req, cmsName, firstName }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [reply, setReply] = useState('')
  const b = STATUS_BADGE[req.status] || STATUS_BADGE.pending
  const closed = req.status === 'completed' || req.status === 'rejected'
  const teamInitial = (cmsName || 'T').slice(0, 1).toUpperCase()
  const youInitial = (firstName || 'Y').slice(0, 1).toUpperCase()

  const { data: messages = [] } = useQuery(
    ['request-messages', req.id],
    () => axios.get(`/requests/${req.id}/messages`).then(r => r.data),
    { staleTime: 60000, refetchOnWindowFocus: false }
  )
  // Show the team's message under the title only while awaiting the client's
  // reply — i.e. the most recent message is from admin. Once the client
  // responds, the preview is hidden.
  const lastMsg = messages[messages.length - 1]
  const teamAwaiting = !!lastMsg && lastMsg.author_role === 'admin'

  const replyMutation = useMutation(
    (body) => axios.post(`/requests/${req.id}/messages`, { body }),
    {
      onSuccess: () => { toast.success('Reply sent'); setReply(''); queryClient.invalidateQueries(['request-messages', req.id]) },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to send reply'),
    }
  )

  // Original request description shown as the first message in the thread
  const thread = [
    req.description ? { id: '__desc', author_role: 'client', author_name: firstName || 'You', body: req.description, created_at: req.created_at } : null,
    ...messages,
  ].filter(Boolean)

  return (
    <div
      onClick={() => setOpen(o => !o)}
      style={{ padding: 14, borderRadius: 12, border: `1px solid ${open ? 'var(--border-strong)' : 'var(--border)'}`, background: req.status === 'pending' ? 'var(--surface)' : 'var(--bg-1)', cursor: 'pointer', transition: 'border-color .14s ease' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span className={`badge badge-${b.tone}`}>{b.label}</span>
        {/* Flexible spacer toggles the card; only the title text navigates */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <span
            onClick={(e) => { e.stopPropagation(); navigate(`/client/requests?open=${req.id}`) }}
            title="Open in Requests"
            style={{ display: 'inline-block', maxWidth: '100%', fontSize: 13, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom', cursor: 'pointer' }}
          >
            {req.title}
          </span>
        </div>
        <span style={{ fontSize: 11.5, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)' }}>{timeAgo(req.created_at)}</span>
      </div>

      {/* Collapsed: last message from the team (hidden once the client replies) */}
      {!open && teamAwaiting && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'flex-start' }}>
          <div className="avatar avatar-grad-1" style={{ width: 22, height: 22, fontSize: 10 }}>{teamInitial}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 2 }}><strong style={{ color: 'var(--text-soft)' }}>{cmsName} team</strong></div>
            <div style={{ fontSize: 12.5, color: 'var(--text-soft)', lineHeight: 1.45, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{lastMsg.body}</div>
          </div>
        </div>
      )}

      {/* Expanded: thread + inline quick reply */}
      {open && (
        <div style={{ marginTop: 12 }} onClick={(e) => e.stopPropagation()}>
          {thread.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
              {thread.slice(-4).map((m) => {
                const mine = m.author_role === 'client'
                return (
                  <div key={m.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexDirection: mine ? 'row-reverse' : 'row' }}>
                    <div className={`avatar ${mine ? 'avatar-grad-4' : 'avatar-grad-1'}`} style={{ width: 22, height: 22, fontSize: 10 }}>{mine ? youInitial : teamInitial}</div>
                    <div style={{ maxWidth: '80%' }}>
                      <div style={{ fontSize: 12.5, lineHeight: 1.45, padding: '8px 10px', borderRadius: 10, background: mine ? 'var(--accent-soft)' : 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }}>{m.body}</div>
                      <div style={{ fontSize: 10.5, color: 'var(--text-faint)', marginTop: 3, textAlign: mine ? 'right' : 'left' }}>{mine ? (m.author_name || 'You') : `${cmsName} team`}{m.created_at ? ` · ${timeAgo(m.created_at)}` : ''}</div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {closed ? (
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              This request is {req.status}.{' '}
              <span onClick={() => navigate(`/client/requests?open=${req.id}`)} style={{ color: 'var(--accent-3, var(--accent))', cursor: 'pointer', textDecoration: 'underline' }}>View full thread</span>
            </div>
          ) : (
            <div>
              <textarea
                className="input"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Type a quick reply…"
                style={{ minHeight: 56, resize: 'vertical' }}
              />
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <button className="btn btn-sm btn-ghost" onClick={() => navigate(`/client/requests?open=${req.id}`)}>Open full thread</button>
                <button className="btn btn-sm btn-primary" style={{ marginLeft: 'auto' }} disabled={!reply.trim() || replyMutation.isLoading} onClick={() => reply.trim() && replyMutation.mutate(reply.trim())}>
                  <IcSend /> {replyMutation.isLoading ? 'Sending…' : 'Send'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────
const ClientOverview = () => {
  const { user } = useAuth()
  const { cmsName } = useBranding()
  const navigate = useNavigate()
  const [showOnboardingModal, setShowOnboardingModal] = useState(false)
  const [showNewRequest, setShowNewRequest] = useState(false)
  const { onboardingData } = useOnboarding()

  // ── Preserved API calls ───────────────────────────────────────────────────
  const { data: profile } = useQuery(
    'client-profile',
    () => axios.get('/clients/profile').then(res => res.data),
    { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false }
  )

  const { data: sitesData } = useQuery(
    'client-sites',
    () => axios.get('/sites').then(res => res.data),
    { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false }
  )

  const { data: requestsData } = useQuery(
    'client-requests',
    () => axios.get('/requests?limit=10').then(res => res.data),
    { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false }
  )

  const { data: analyticsData } = useQuery(
    ['client-analytics', '30d'],
    () => axios.get('/analytics/overview?range=30d').then(r => r.data),
    { staleTime: 120000 }
  )

  // Resolve the plan's friendly name + price (custom plans use a UUID id)
  const { data: plansRaw } = useQuery(
    'billing-plans',
    () => axios.get('/billing/plans').then(r => r.data),
    { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false }
  )

  // ── Onboarding modal: show once per session until onboarding is complete ────
  useEffect(() => {
    if (user?.role !== 'client' || !onboardingData) return
    if (onboardingData.onboardingCompleted) return
    if (onboardingStorage.wasModalSeen() || onboardingStorage.isSkippedForSession()) return
    setShowOnboardingModal(true)
    onboardingStorage.markModalSeen()
  }, [user, onboardingData])

  // ── Derived values ────────────────────────────────────────────────────────
  const sites       = sitesData?.sites || (Array.isArray(sitesData) ? sitesData : [])
  const requests    = Array.isArray(requestsData) ? requestsData : (requestsData?.requests || [])
  const firstSite   = sites.length > 0 ? sites[0] : null
  const domain      = firstSite?.domain || profile?.company_name || 'your site'
  const isLive      = firstSite?.status === 'published' || firstSite?.status === 'live'

  const totalPageViews = analyticsData?.totalPageViews || 0
  const uniqueVisitors = analyticsData?.uniqueVisitors  || 0
  const activityData   = analyticsData?.activityData    || []
  const topPages       = analyticsData?.topPages        || []
  const sparklineNums  = activityData.map(d => d.views || d.count || 0)

  const editsUsed  = profile?.edits_used    || 0
  const editQuota  = profile?.edit_quota    || 50
  const storageMb  = Math.round(profile?.storage_used_mb || 0)
  const siteCount  = sites.length
  const siteLimit  = profile?.site_limit || 3
  const openReqs   = requests.filter(r => !['completed', 'rejected'].includes(r.status)).length
  const doneReqs   = requests.filter(r => r.status === 'completed').length

  // Plan name + price (handles custom-plan UUIDs)
  const planObj   = plansRaw && profile?.subscription_plan ? plansRaw[profile.subscription_plan] : null
  const planName  = planObj?.name
    || (profile?.subscription_plan && ['basic', 'pro', 'enterprise'].includes(profile.subscription_plan)
        ? profile.subscription_plan.charAt(0).toUpperCase() + profile.subscription_plan.slice(1)
        : (profile?.subscription_plan ? 'Custom plan' : 'Basic'))
  const planPrice = planObj?.price
  const isFreePlan = planPrice === 0

  const onboardingSteps = [
    { label: 'Connect your custom domain', done: !!firstSite?.domain,             to: '/client/domains' },
    { label: 'Add your business info',     done: !!profile?.company_name,          to: '/client/settings' },
    { label: 'Upload your brand logo',     done: !!profile?.logo,                  to: '/client/settings?tab=brand' },
    { label: 'Customize your site',        done: firstSite?.status === 'published', to: '/client/editor' },
  ]
  const onboardingDone = onboardingSteps.filter(s => s.done).length
  const showOnboarding = onboardingDone < onboardingSteps.length

  // Completed requests aren't surfaced on the dashboard
  const recentRequests = requests.filter(r => r.status !== 'completed').slice(0, 3)
  const firstName = (user?.name || profile?.user_name || '').trim().split(' ')[0]

  return (
    <>
      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Your account</div>
          <h1>Hey{firstName ? ` ${firstName}` : ''} 👋</h1>
          <div className="subtitle">
            Everything that matters about <strong style={{ color: 'var(--text)' }}>{domain}</strong>, in one place.
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <Link to="/client/help"><button className="btn"><IcHelp /> Help</button></Link>
        </div>
      </div>

      <div className="page-body" style={{ gap: 18, paddingBottom: 40 }}>
        {/* ── Hero ───────────────────────────────────────────────────────── */}
        <div className="client-hero">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5 }}>
            <span className={`badge badge-${isLive ? 'success' : 'warning'}`}><span className="badge-dot" /> {isLive ? 'Site is live' : 'Draft mode'}</span>
            <span style={{ color: 'var(--text-muted)' }}>{domain}</span>
            {firstSite?.domain && (
              <a href={`https://${firstSite.domain}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-muted)', display: 'inline-flex' }}>
                <IcExternal />
              </a>
            )}
          </div>

          <h1>{uniqueVisitors > 0 ? 'Your site is doing great this month.' : 'Welcome to your dashboard.'}</h1>
          <div style={{ fontSize: 15, color: 'var(--text-soft)', maxWidth: 600, lineHeight: 1.55 }}>
            {uniqueVisitors > 0 ? (
              <>{uniqueVisitors.toLocaleString()} people visited your site recently.{' '}
                {openReqs > 0
                  ? `${openReqs} edit request${openReqs === 1 ? '' : 's'} in progress.`
                  : 'You’re all caught up on requests.'}</>
            ) : (
              'Your workspace is ready — edit your site, request a change, or check your stats below.'
            )}
          </div>

          <div className="client-cta-row">
            <button className="cta-tile primary" onClick={() => navigate('/client/editor')}>
              <span className="cta-tile-icon"><IcEdit /></span>
              <span className="cta-tile-text">
                <span className="cta-tile-label">Edit your website</span>
                <span className="cta-tile-sub">Open the visual editor</span>
              </span>
            </button>
            <button className="cta-tile" onClick={() => setShowNewRequest(true)}>
              <span className="cta-tile-icon"><IcMessage /></span>
              <span className="cta-tile-text">
                <span className="cta-tile-label">Request a change</span>
                <span className="cta-tile-sub">We'll handle it for you</span>
              </span>
            </button>
            <button className="cta-tile" onClick={() => navigate('/client/analytics')}>
              <span className="cta-tile-icon"><IcChart /></span>
              <span className="cta-tile-text">
                <span className="cta-tile-label">See visitor stats</span>
                <span className="cta-tile-sub">Last 30 days</span>
              </span>
            </button>
          </div>

          <div style={{ position: 'absolute', right: -60, top: -60, width: 220, height: 220, borderRadius: '50%', background: 'var(--accent-soft)', pointerEvents: 'none', filter: 'blur(40px)' }} />
        </div>

        {/* ── Stat snapshot ──────────────────────────────────────────────── */}
        <div className="bento" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
          <SoftStat label="Visitors this month" value={uniqueVisitors > 0 ? uniqueVisitors.toLocaleString() : '—'} />
          <SoftStat label="Page views" value={totalPageViews > 0 ? totalPageViews.toLocaleString() : '—'} />
          <SoftStat label="Edit requests" value={requests.length} sub={`${doneReqs} done, ${openReqs} in progress`} />
          <SoftStat label="Storage used" value={`${storageMb} MB`} sub="of 2 GB · plenty of room" progress={Math.min((storageMb / 2048) * 100, 100)} />
        </div>

        {/* ── Requests + visitors ────────────────────────────────────────── */}
        <div className="bento" style={{ gridTemplateColumns: '1.4fr 1fr', gap: 14, alignItems: 'stretch' }}>
          {/* Requests */}
          <div className="card card-pad">
            <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
              <div>
                <h2 className="h2">Your requests</h2>
                <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4 }}>Typical first reply in under 4 hours.</div>
              </div>
              <button className="btn btn-sm btn-primary" onClick={() => setShowNewRequest(true)}><IcPlus /> New request</button>
            </div>

            {recentRequests.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-faint)' }}>
                <IcMessage />
                <div style={{ marginTop: 10, fontSize: 13 }}>No requests yet</div>
                <button className="btn btn-sm btn-primary" style={{ marginTop: 12 }} onClick={() => setShowNewRequest(true)}>Create your first request</button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {recentRequests.map(r => <ClientRequestRow key={r.id} req={r} cmsName={cmsName} firstName={firstName} />)}
                <Link to="/client/requests" style={{ fontSize: 12, color: 'var(--accent-3, var(--accent))', display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 2, textDecoration: 'none' }}>
                  View all requests <IcArrow />
                </Link>
              </div>
            )}
          </div>

          {/* Visitors */}
          <div className="card card-pad">
            <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
              <div>
                <h2 className="h2">Visitors</h2>
                <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4 }}>Last 30 days · {domain}</div>
              </div>
              <Link to="/client/analytics" style={{ fontSize: 12, color: 'var(--accent-3, var(--accent))', display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none' }}>
                Full analytics <IcArrow />
              </Link>
            </div>
            <div className="stat-row">
              <div className="num">{uniqueVisitors > 0 ? uniqueVisitors.toLocaleString() : '—'}</div>
            </div>
            <div style={{ marginTop: 12, marginBottom: 10 }}>
              <Sparkline data={sparklineNums} />
            </div>

            <div className="divider" />
            <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>Top pages</div>
            {topPages.length === 0 ? (
              <div style={{ fontSize: 12.5, color: 'var(--text-faint)' }}>No visits yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {topPages.slice(0, 4).map((p, i) => {
                  const maxViews = Math.max(...topPages.map(x => x.views), 1)
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                      <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-soft)' }}>{p.page}</span>
                      <div className="progress thin" style={{ flex: 1 }}>
                        <div style={{ width: `${(p.views / maxViews) * 100}%` }} />
                      </div>
                      <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', fontSize: 11.5, width: 40, textAlign: 'right' }}>{p.views.toLocaleString()}</span>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── Domain + plan ──────────────────────────────────────────────── */}
        <div className="bento" style={{ gridTemplateColumns: '1fr 1fr', gap: 14, alignItems: 'stretch' }}>
          {/* Domain & SSL */}
          <div className="card card-pad">
            <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
              <h2 className="h2">Domain & SSL</h2>
              <span className="badge badge-success"><IcShield /> Secure</span>
            </div>
            <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 4, wordBreak: 'break-all' }}>{firstSite?.domain || domain}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>Auto-renews annually</div>
            <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                { l: 'DNS records', v: 'Healthy', ok: true },
                { l: 'SSL certificate', v: 'Valid', ok: true },
              ].map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                  <span className={`icon-tile ${r.ok ? 'success' : 'warning'}`} style={{ width: 20, height: 20, fontSize: 10 }}>
                    {r.ok ? <IcCheck /> : <span style={{ fontSize: 9, fontWeight: 700 }}>!</span>}
                  </span>
                  <span style={{ color: 'var(--text-soft)', flex: 1 }}>{r.l}</span>
                  <span style={{ color: r.ok ? 'var(--text-muted)' : 'var(--warning)', fontSize: 12 }}>{r.v}</span>
                </div>
              ))}
            </div>
            <button className="btn btn-sm" style={{ marginTop: 14, width: '100%' }} onClick={() => navigate('/client/domains')}>Manage domain</button>
          </div>

          {/* Plan */}
          <div className="card card-pad">
            <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
              <h2 className="h2">Your plan</h2>
              <span className="badge badge-accent"><IcBolt /> {planName}</span>
            </div>
            <div style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.02em', fontFamily: 'var(--font-mono)' }}>
              {planPrice != null ? <>£{planPrice}</> : '—'}
              <span style={{ fontSize: 13, color: 'var(--text-muted)', fontFamily: 'var(--font)' }}> /month</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              {isFreePlan ? 'No payment required · custom plan' : 'Renews monthly · manage in billing'}
            </div>

            <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <UsageBar label="Edit requests" used={editsUsed} total={editQuota === 'unlimited' ? editsUsed || 1 : editQuota} />
              <UsageBar label="Storage" used={(storageMb / 1024).toFixed(1)} total={2} unit=" GB" />
              <UsageBar label="Sites" used={siteCount} total={siteLimit} />
            </div>
            <button className="btn btn-sm" style={{ marginTop: 14, width: '100%' }} onClick={() => navigate('/client/billing')}>Manage plan</button>
          </div>
        </div>

        {/* ── Onboarding checklist ───────────────────────────────────────── */}
        {showOnboarding && (
          <div className="card card-pad">
            <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
              <div>
                <h2 className="h2">Get the most out of {cmsName}</h2>
                <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4 }}>
                  {onboardingDone} of {onboardingSteps.length} complete · Finish setup to unlock everything
                </div>
              </div>
              <div className="progress" style={{ width: 120 }}>
                <div style={{ width: `${(onboardingDone / onboardingSteps.length) * 100}%` }} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
              {onboardingSteps.map((c, i) => (
                <Link key={i} to={c.to} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 10, background: c.done ? 'var(--surface)' : 'var(--bg-1)', textDecoration: 'none' }}>
                  <span className={`icon-tile ${c.done ? 'success' : ''}`} style={{ width: 22, height: 22, fontSize: 11 }}>
                    {c.done ? <IcCheck /> : <span style={{ width: 8, height: 8, borderRadius: 4, background: 'var(--text-faint)' }} />}
                  </span>
                  <span style={{ fontSize: 12.5, color: c.done ? 'var(--text-muted)' : 'var(--text)', textDecoration: c.done ? 'line-through' : 'none', flex: 1 }}>{c.label}</span>
                  {!c.done && <IcArrow />}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Onboarding modal */}
      {showOnboardingModal && (
        <OnboardingModal
          isOpen={showOnboardingModal}
          onComplete={() => setShowOnboardingModal(false)}
          onClose={() => {
            setShowOnboardingModal(false)
            onboardingStorage.setSkippedForSession && onboardingStorage.setSkippedForSession(true)
          }}
        />
      )}

      {/* New request modal */}
      {showNewRequest && <NewRequestModal onClose={() => setShowNewRequest(false)} />}
    </>
  )
}

export default ClientOverview
